// src/pages/Notifications/NotificationPage.jsx

import React from "react";
import "@styles/opencart-admin.css";
import Loader from "@components/Loader";
import {
  useNotificationPreviewMutation,
  useNotificationSendMutation
} from "@queries/notification";
import { useCustomerGroupsQuery } from "@queries/customers/customer_groups";

const defaultForm = {
  title_en: "",
  message_en: "",
  title_ar: "",
  message_ar: "",
  deep_link: "",
  customer_group_id: "",
  test_customer_id: "",
  platform: "both"
};

const deepLinks = [
  { value: "home?service_id=1", label: "Homepage" },
  { value: "category?cat_id=184", label: "Eco Carpets" },
  { value: "category?cat_id=76", label: "Eco Dresses" },
  { value: "category?cat_id=79", label: "Eco Suits" },
  { value: "category?cat_id=33", label: "Eco Tops" },
  { value: "category?cat_id=102", label: "Iron Suits" },
  { value: "category?cat_id=98", label: "Iron Tops" },
  { value: "category?cat_id=90", label: "Eco Bedding" },
  { value: "category?cat_id=180", label: "Eco Shoes" },
  { value: "category?cat_id=81", label: "Eco Outdoor" },
  { value: "category?cat_id=137", label: "Wash & Fold" },
  { value: "category?cat_id=199", label: "Offers" },
  { value: "/home?service_Id=232", label: "Car Cleaning" }
];

function NotificationPage() {
  const [form, setForm] = React.useState(defaultForm);
  const [error, setError] = React.useState("");
  const [previewResult, setPreviewResult] = React.useState(null);
  const [sendResult, setSendResult] = React.useState(null);

  const previewMutation = useNotificationPreviewMutation();
  const sendMutation = useNotificationSendMutation();

  const {
    data: customerGroupsData,
    isLoading: isGroupsLoading,
    isError: isGroupsError
  } = useCustomerGroupsQuery({},1, 100);

  const customerGroups = customerGroupsData?.data || [];

  const isBusy =
    previewMutation.isLoading ||
    sendMutation.isLoading ||
    previewMutation.isPending ||
    sendMutation.isPending;

  function buildPayload() {
    const payload = {
      title_en: form.title_en,
      message_en: form.message_en,
      deep_link: form.deep_link,
      platform: form.platform || "both"
    };

    if (form.title_ar) payload.title_ar = form.title_ar;
    if (form.message_ar) payload.message_ar = form.message_ar;
    if (form.customer_group_id) {
      payload.customer_group_id = Number(form.customer_group_id);
    }
    if (form.test_customer_id) {
      payload.test_customer_id = Number(form.test_customer_id);
    }

    return payload;
  }

  function validateForm() {
    if (!form.title_en.trim()) return "English title is required.";
    if (!form.message_en.trim()) return "English message is required.";
    if (!form.deep_link.trim()) return "Deep link is required.";
    return "";
  }

  async function handlePreview(e) {
    e.preventDefault();
    setError("");
    setSendResult(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    const payload = buildPayload();
    try {
      const data = await previewMutation.mutateAsync(payload);
      setPreviewResult(data);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to preview notification.");
    }
  }

  async function handleSend(e) {
    e.preventDefault();
    setError("");
    setSendResult(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    const payload = buildPayload();
    try {
      const data = await sendMutation.mutateAsync(payload);
      setSendResult(data);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to send notification.");
    }
  }

  function handleChange(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function handleDeepLinkSelect(value) {
    if (!value) return;
    setForm((prev) => ({ ...prev, deep_link: value }));
  }

  function resetForm() {
    setForm(defaultForm);
    setError("");
    setPreviewResult(null);
    setSendResult(null);
  }

  function renderCustomerGroupOptions() {
    if (isGroupsLoading) {
      return (
        <option value="" disabled>
          Loading groups…
        </option>
      );
    }

    if (isGroupsError || customerGroups.length === 0) {
      return (
        <option value="">
          All customers (no groups found)
        </option>
      );
    }

    return (
      <>
        <option value="">All customers (all groups)</option>
        {customerGroups.map((group) => {
          const en = group.descriptions?.find(
            (d) => d.language_id === 1
          );
          const ar = group.descriptions?.find(
            (d) => d.language_id === 2
          );
          const name =
            en?.name ||
            ar?.name ||
            `Group ${group.customer_group_id}`;

          return (
            <option
              key={group.customer_group_id}
              value={group.customer_group_id}
            >
              {name} (#{group.customer_group_id})
            </option>
          );
        })}
      </>
    );
  }

  return (
    <div className="oc-content">
      {isBusy && <Loader />}

      <div className="oc-header">
        <h1>Custom Notifications</h1>
        <nav className="oc-bc">
          <span>Home</span>
          <span className="sep">›</span>
          <span>Notifications</span>
        </nav>
      </div>

      <div className="oc-card">
        <div className="oc-toolbar" style={{ marginBottom: 12 }}>
          <div>
            <strong>Send push notification</strong>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="oc-btn ghost"
              type="button"
              onClick={resetForm}
              disabled={isBusy}
            >
              Reset
            </button>
          </div>
        </div>

        {error && (
          <div style={{ color: "#c00", marginBottom: 8 }}>
            {error}
          </div>
        )}

        <form>
          <div className="form-row">
            <div className="form-group">
              <label>English title</label>
              <input
                className="oc-input"
                value={form.title_en}
                onChange={(e) => handleChange("title_en", e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>Arabic title</label>
              <input
                className="oc-input"
                dir="rtl"
                value={form.title_ar}
                onChange={(e) => handleChange("title_ar", e.target.value)}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>English message</label>
              <textarea
                className="oc-input"
                rows={3}
                value={form.message_en}
                onChange={(e) => handleChange("message_en", e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>Arabic message</label>
              <textarea
                className="oc-input"
                dir="rtl"
                rows={3}
                value={form.message_ar}
                onChange={(e) => handleChange("message_ar", e.target.value)}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Deep link</label>
              <input
                className="oc-input"
                value={form.deep_link}
                onChange={(e) => handleChange("deep_link", e.target.value)}
                placeholder="home?service_id=1"
                required
              />
            </div>
            <div className="form-group">
              <label>Quick deep link</label>
              <select
                className="oc-input"
                value=""
                onChange={(e) => handleDeepLinkSelect(e.target.value)}
              >
                <option value="">Select…</option>
                {deepLinks.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label} ({item.value})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Customer group</label>
              <select
                className="oc-input"
                value={form.customer_group_id}
                onChange={(e) =>
                  handleChange("customer_group_id", e.target.value)
                }
                disabled={isGroupsLoading}
              >
                {renderCustomerGroupOptions()}
              </select>
              <small style={{ color: "#666", display: "block", marginTop: 4 }}>
                Leave empty to send to all groups. When set, notifications will
                be limited to this group.
              </small>
            </div>
            <div className="form-group">
              <label>Test customer ID</label>
              <input
                className="oc-input"
                value={form.test_customer_id}
                onChange={(e) =>
                  handleChange("test_customer_id", e.target.value)
                }
                placeholder="send only to this customer"
              />
              <small style={{ color: "#666", display: "block", marginTop: 4 }}>
                If set, overrides group and sends only to this customer.
              </small>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Platform</label>
              <select
                className="oc-input"
                value={form.platform}
                onChange={(e) => handleChange("platform", e.target.value)}
              >
                <option value="both">Android + iOS</option>
                <option value="android">Android only</option>
                <option value="ios">iOS only</option>
              </select>
            </div>
            <div className="form-group" style={{ alignSelf: "flex-end" }}>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button
                  type="button"
                  className="oc-btn"
                  onClick={handlePreview}
                  disabled={isBusy}
                >
                  Preview counts
                </button>
                <button
                  type="button"
                  className="oc-btn primary"
                  onClick={handleSend}
                  disabled={isBusy}
                >
                  Send now
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>

      <div className="oc-card" style={{ marginTop: 16 }}>
        <div className="title">Preview</div>
        {!previewResult && (
          <div style={{ padding: 12, color: "#777" }}>
            Click "Preview counts" to see how many devices will receive this
            notification.
          </div>
        )}
        {previewResult && (
          <table className="oc-table">
            <thead>
              <tr>
                <th>Platform</th>
                <th>Language</th>
                <th>Devices</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Android</td>
                <td>English</td>
                <td>{previewResult.counts?.android_en ?? 0}</td>
              </tr>
              <tr>
                <td>Android</td>
                <td>Arabic</td>
                <td>{previewResult.counts?.android_ar ?? 0}</td>
              </tr>
              <tr>
                <td>iOS</td>
                <td>English</td>
                <td>{previewResult.counts?.ios_en ?? 0}</td>
              </tr>
              <tr>
                <td>iOS</td>
                <td>Arabic</td>
                <td>{previewResult.counts?.ios_ar ?? 0}</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>

      <div className="oc-card" style={{ marginTop: 16 }}>
        <div className="title">Send status</div>
        {!sendResult && (
          <div style={{ padding: 12, color: "#777" }}>
            After sending, status will appear here.
          </div>
        )}
        {sendResult && (
          <div style={{ padding: 12 }}>
            <div>
              Status: <strong>{sendResult.status}</strong>
            </div>
            {typeof sendResult.jobs !== "undefined" && (
              <div>Queued jobs: {sendResult.jobs}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default NotificationPage;

